import { Form, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyService from 'services/CompanyService';
import CountriesService from 'services/CountriesService';
import { CompanyModel, CountryModel, RegionModel, CurrencySelectModel, RegionSelectModel } from 'services/EpdClient';
import styled, { useTheme } from 'styled-components';
import { ErrorText, HyperLink, InfoText, SaveButton, SuccessText } from 'styles/Styles.styled';
import { Button as PrimeButton } from 'primereact/button';
import * as Yup from 'yup';
import AuthService from 'services/AuthService';
import { ActionState, UpdateCompanyInvoiceInfoModel } from 'services/EpdClient';
import { EU_COUNTRIES_NAMES, ProcessStatus, VAT_VALIDATION_SERVICE_LINK } from '../../constants/constants';
import { toaster } from 'components/v2/toast';
import TextInput from '../form/TextInput';
import InputFormField from 'components/v2/form/InputFormField';
import { useGetRegexVATPattern } from './useGetRegexVATPattern';
import VatValidationService from 'services/VatValidationService';
import { YES_NO, YesNoDataSource } from 'components/v2/compiler/constants';
import AvailableCountryCurrencies from './AvailableCountryCurrencies';
import PaymentTerms from './PaymentTerms';
import { useDictionaryValues } from 'services/api/queries';
import calculateCurrencyOptions from './calculateCurrencyOptions';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { Theme } from 'styles/theme';
import { Option } from 'types/types';
import { registerCompanySelectStyles } from 'components/form/EditSelectStyles';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import {
  ALLOWED_SYMBOLS_ERROR,
  allowedGeneralCharsRegex,
  allowedSymbolsLink,
  emailSmartValidation,
  renderAllowedSymbolsError,
} from 'util/allowedSymbolsValidationUtils';
import { HelpBox } from 'components/v2/help-boxes';
import { EpdLinks } from 'routes/EpdRoutes';
import { withActionState } from 'pages/v2/WithActionState';

type FormikCompanyModel = Omit<
  CompanyModel,
  | 'invoicingEmail'
  | 'invoiceOrderingEmail'
  | 'invoiceRemindingEmail'
  | 'invoiceCcEmail'
  | 'specialInvoiceNeeds'
  | 'reference'
> &
  CurrencySelectModel &
  RegionSelectModel & {
    invoicingEmail?: string | null;
    invoiceOrderingEmail?: string | null;
    invoiceRemindingEmail?: string | null;
    invoiceCcEmail?: string | null;
    specialInvoiceNeeds?: string | null;
    reference?: string | null;

    termsOfPaymentOption: Option | undefined;
    isSpecialInvoiceNeedsOption: Option | undefined;
    isInvoiceUniquePurchaseOrderNumberOption: Option | undefined;
  };

const ManageInvoiceInfo: React.FunctionComponent<{ companyId: string }> = ({ companyId }) => {
  const theme = useTheme() as Theme;

  const [company, setCompany] = useState<FormikCompanyModel>({} as FormikCompanyModel);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [regions, setRegions] = useState<RegionModel[]>([]);
  const { t } = useTranslation();
  const allCountriesRef = useRef<CountryModel[]>([]);
  const glnValidationRegex = /^\d{13}$/;
  const eurCurrencyCountries = useDictionaryValues(EPD_DICTIONARY_CODES.EUR_CURRENCY_COUNTRIES);

  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  const fetchCompany = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await CompanyService.getCompany(companyId);
      setCountries(allCountriesRef.current);
      setSelectedCountry(result.invoiceCountryName);

      const currentRegions = await CountriesService.getCountryRegions();
      setRegions(currentRegions);

      const invoiceCountry = allCountriesRef.current.find((x) => x.id === result.invoiceCountryId);
      const invoiceRegion = currentRegions.find((x) => x.id === result.invoiceRegionId);
      setCompany({
        ...result,
        invoiceCountryOption: invoiceCountry ? { value: invoiceCountry?.id, label: invoiceCountry?.name } : null,
        invoiceRegionOption: invoiceRegion ? { value: invoiceRegion?.id, label: invoiceRegion?.name } : null,
        preferredCurrencyOption: { value: result.preferredCurrency, label: result.preferredCurrency } as Option,
        termsOfPaymentOption: { value: result.termsOfPayment, label: result.termsOfPayment },
        isSpecialInvoiceNeedsOption: {
          value: result.isSpecialInvoiceNeeds,
          label: YesNoDataSource.find((x) => x.value == result.isSpecialInvoiceNeeds)?.label ?? '',
        },
        isInvoiceUniquePurchaseOrderNumberOption: {
          value: result.isInvoiceUniquePurchaseOrderNumber,
          label: YesNoDataSource.find((x) => x.value == result.isInvoiceUniquePurchaseOrderNumber)?.label ?? '',
        },
      } as FormikCompanyModel);
    } catch (error) {}
    setStatus(ProcessStatus.None);
  };
  const isAdmin = AuthService.isAdmin();
  useEffect(() => {
    async function fetch() {
      if (!allCountriesRef.current || allCountriesRef.current.length == 0) {
        const countries = await CountriesService.getCountries();
        allCountriesRef.current = countries; //setAllCountries(countries);
        setCountries(countries);
      }
    }
    fetch();
  }, []);

  const handleCountryChange = (selectedOption: Option, values: FormikCompanyModel, setValues: any, setFieldValue: any) => {
    const country = countries.find((c) => c.id === selectedOption.value);
    if (!country) {
      setFieldValue('invoiceCountryOption', undefined);
      return;
    }
    const countryCode = country.alpha2Code;
    setSelectedCountry(country.name);

    const valuesPatch: Partial<FormikCompanyModel> = {
      invoiceCountryOption: selectedOption,
      invoiceRegionOption: null,
    };
    if (eurCurrencyCountries.data && countryCode) {
      const options = calculateCurrencyOptions(
        countryCode,
        eurCurrencyCountries.data.map((c) => c.value)
      );
      valuesPatch.preferredCurrencyOption = options[0];
    }

    // Separate setFieldValue calls each validate against a stale values snapshot and race each
    // other, leaving isValid wrong until the next blur. Update everything in one pass instead.
    setValues({ ...values, ...valuesPatch });
  };

  const handleSpecialNeedsChange = (selectedOption: any, setFieldValue: any) => {
    if (selectedOption?.value === YES_NO.NO) {
      setFieldValue('specialInvoiceNeeds', '');
    }
    return selectedOption;
  };

  const updateCompany = async (values: FormikCompanyModel) => {
    try {
      const invoiceInfoModel: UpdateCompanyInvoiceInfoModel = {
        id: values.id,
        invoiceOrganizationName: values.invoiceOrganizationName,
        invoiceAddressLine: values.invoiceAddressLine,
        invoiceAddressLineSecond: values.invoiceAddressLineSecond,
        invoiceAddressCity: values.invoiceAddressCity,
        invoiceAddressZipCode: values.invoiceAddressZipCode,
        invoiceCountryId: values.invoiceCountryOption?.value,
        invoiceRegionId: values.invoiceRegionOption?.value,
        invoiceOrderingEmail: values.invoiceOrderingEmail ?? undefined,
        invoiceRemindingEmail: values.invoiceRemindingEmail ?? undefined,
        invoicingEmail: values.invoicingEmail ?? undefined,
        invoiceCcEmail: values.invoiceCcEmail ?? undefined,
        vat: values.vat,
        gln: values.gln,
        specialInvoiceNeeds: values.specialInvoiceNeeds ?? undefined,
        reference: values.reference ?? undefined,
        isInvoiceUniquePurchaseOrderNumber: values.isInvoiceUniquePurchaseOrderNumberOption?.value ?? YES_NO.NO,
        isSpecialInvoiceNeeds: values.isSpecialInvoiceNeedsOption?.value ?? YES_NO.NO,
        preferredCurrency: values.preferredCurrencyOption?.value,
        termsOfPayment: values.termsOfPaymentOption?.value,
        needDataUpdating: values.needDataUpdating,
      };
      await CompanyService.updateCompanyInvoiceInfo(companyId, invoiceInfoModel);
      await fetchCompany();
      setStatus(ProcessStatus.Success);
    } catch (error) {
      setStatus(ProcessStatus.Error);
    }
  };

  if (status === ProcessStatus.Fetching) {
    return <InfoText>{t('manageSettings.messages.fetching')}</InfoText>;
  }

  if (!company?.id && status === ProcessStatus.Error) {
    return <ErrorText>{t('manageSettings.errors.technical')}</ErrorText>;
  }
  const sendToFortnox = async () => {
    try {
      await CompanyService.sendCompanyToFortnox(companyId);
      toaster({ severity: 'success', summary: 'Success', details: 'Company info aready updated ' });
    } catch (error) {
      toaster({ severity: 'error', summary: 'Error', details: '' + error });
    }
  };

  const REQUIRED_MSG = t('messages.required');
  const ZIP_LENGTH_ERROR = 'Please enter a ZIP code between 3 and 20 characters long.';
  const invoiceFieldErrorProps = {
    errorPositioning: 'absolute' as const,
    shouldShowBottomError: (err: string) => err !== REQUIRED_MSG,
    shouldHighlightBorder: (err: string) => err !== REQUIRED_MSG,
    renderErrorMessage: (err: string) =>
      err === ALLOWED_SYMBOLS_ERROR ? renderAllowedSymbolsError({ theme, allowedSymbolsLink }) : err,
  };

  const companySchema = Yup.object<FormikCompanyModel>({
    invoiceOrganizationName: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    invoiceAddressLine: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    invoiceAddressLineSecond: Yup.string()
      .nullable()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR) as Yup.StringSchema,
    invoiceAddressCity: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    invoiceAddressZipCode: Yup.string()
      .nullable()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .transform((value) => value?.trim())
      .min(3, ZIP_LENGTH_ERROR)
      .max(20, ZIP_LENGTH_ERROR),
    invoiceCountryOption: Yup.object<Option>().nullable().required(t('messages.required')),
    invoiceOrderingEmail: emailSmartValidation(t).nullable().required(t('messages.required')),
    invoiceRemindingEmail: emailSmartValidation(t).nullable().required(t('messages.required')),
    invoicingEmail: emailSmartValidation(t).nullable().required(t('messages.required')),
    invoiceCcEmail: emailSmartValidation(t)
      .nullable()
      .test('invoice-email-match', t('messages.ccInvoiceEmailIsTheSameAsInvoiceEmail'), function (value) {
        if (!value) return true;
        return this.parent.invoicingEmail !== value;
      }),
    vat: (Yup.string() as any)
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when(['invoiceCountryOption', 'invoiceRegionOption'], {
        is: (country: Option, region: Option) => {
          return EU_COUNTRIES_NAMES.includes(country?.label) || EU_COUNTRIES_NAMES.includes(region?.label);
        },
        then: Yup.string()
          .required(t('messages.required'))
          .test('vat-format', 'vat-format-error', function (value) {
            if (!value) return true;

            const { invoiceCountryOption, invoiceRegionOption } = this.parent;

            if (invoiceRegionOption?.label === 'Northern Ireland') {
              const ukVatPattern = useGetRegexVATPattern('Northern Ireland');
              return ukVatPattern ? ukVatPattern.test(value) : true;
            }

            const pattern = useGetRegexVATPattern(invoiceCountryOption?.label ?? '');
            if (pattern) {
              return pattern.test(value);
            }

            return true;
          })
          .test('vat-validation', 'vat-validation-error', async function (value) {
            if (!value) return false;

            const { invoiceCountryOption, invoiceRegionOption } = this.parent;
            let validationPattern: RegExp | null = null;

            if (invoiceRegionOption?.label === 'Northern Ireland') {
              validationPattern = useGetRegexVATPattern(invoiceRegionOption.label);
            } else {
              validationPattern = useGetRegexVATPattern(invoiceCountryOption?.label ?? '');
            }

            if (validationPattern && !validationPattern.test(value)) {
              return true;
            }

            try {
              const result = await VatValidationService.get(value);
              return result.validationStatus !== 'InvalidVat';
            } catch (error) {
              console.warn('VAT validation service error:', error);
              return true;
            }
          }),
        otherwise: Yup.string().nullable(),
      })
      .nullable(),
    gln: (Yup.string() as any)
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when([], {
        is: () => selectedCountry === 'Sweden',
        then: Yup.string().matches(glnValidationRegex as RegExp, 'Invalid GLN format') as Yup.StringSchema,
      })
      .nullable(),
    needDataUpdating: Yup.boolean() as any,
    specialInvoiceNeeds: Yup.string()
      .nullable()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when(['isEpdOwner', 'isSpecialInvoiceNeedsOption'], {
        is: (isEpdOwner: boolean | undefined, isSpecialInvoiceNeedsOption: Option | undefined) =>
          isEpdOwner === true && isSpecialInvoiceNeedsOption?.value === YES_NO.YES,
        then: (schema: any) => schema.required(t('messages.required')),
        otherwise: (schema: any) => schema.notRequired(),
      }),
    preferredCurrencyOption: Yup.object<Option>().nullable().required(t('messages.required')),
    termsOfPaymentOption: Yup.object<Option>().nullable().required(t('messages.required')),
    isSpecialInvoiceNeedsOption: Yup.object<Option>().nullable().required(t('messages.required')),
    isInvoiceUniquePurchaseOrderNumberOption: Yup.object<Option>().nullable().required(t('messages.required')),
    invoiceRegionOption: Yup.mixed<Option>()
      .nullable()
      .when('invoiceCountryOption', {
        is: (country: Option) => {
          const countryId = country?.value;
          const availableRegions = regions.filter((r) => r.countryId === countryId);
          return availableRegions.length > 0;
        },
        then: Yup.object<Option>().required(t('messages.required')),
        otherwise: Yup.object<Option>().nullable(),
      }),
    countryOption: Yup.mixed<Option>().nullable(),
    reference: Yup.string().nullable().matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
  });

  const vatFieldErrorProps = {
    errorPositioning: 'absolute' as const,
    shouldShowBottomError: (err: string) => err !== REQUIRED_MSG,
    shouldHighlightBorder: (err: string) => err !== REQUIRED_MSG,
    getErrorTooltip: (err: string) => {
      switch (err) {
        case 'vat-format-error':
          return 'Format error: VAT number must start with a country code followed by numbers, using only letters and digits, no spaces.';
        case 'vat-validation-error':
          return 'VAT number is not registered. Verify here: VIES VAT Checker.';
        default:
          return err;
      }
    },
    renderErrorMessage: (err: string) => {
      switch (err) {
        case ALLOWED_SYMBOLS_ERROR:
          return renderAllowedSymbolsError({ theme, allowedSymbolsLink });

        case 'vat-format-error':
          return (
            <span style={{ color: theme.colors.systemStatusError, fontFamily: 'Inter', fontSize: '12px' }}>
              Format error: VAT number must start with a country code followed by numbers, using only letters and digits, no
              spaces.
            </span>
          );

        case 'vat-validation-error':
          return (
            <span style={{ color: theme.colors.systemStatusError, fontFamily: 'Inter', fontSize: '12px' }}>
              VAT number is not registered. Verify here:{' '}
              <a
                href={VAT_VALIDATION_SERVICE_LINK}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                VIES VAT Checker
              </a>
              .
            </span>
          );

        default:
          return err;
      }
    },
  };

  // Marks every field touched from the start so validation errors show right after the form loads, not only after blur.
  const initialTouched = Object.keys(company).reduce((touched, key) => {
    touched[key] = true;
    return touched;
  }, {} as Record<string, boolean>);

  return (
    <>
      <HelpBox>
        <span>
          Review your invoices and manage your billing details directly from the{' '}
          <HyperLink href={EpdLinks.manageCompany(companyId, 'invoiceList')}>Invoices page</HyperLink>.
        </span>
      </HelpBox>
      <h3 style={{ marginTop: '30px' }}>{t('invoiceInfo.title')}</h3>{' '}
      <InvoiceInfoHelpBox>
        <span>{t('invoiceInfo.info.info1')}</span>
        <ActionUpdateButton
          label="Update in Fortnox"
          onClick={() => sendToFortnox()}
          actionState={isAdmin ? ActionState.Enabled : ActionState.Hidden}
        />
      </InvoiceInfoHelpBox>
      <Formik
        initialValues={company}
        initialTouched={initialTouched as any}
        validationSchema={companySchema}
        onSubmit={(values) => updateCompany(values)}
        validateOnMount
        validateOnBlur
      >
        {({ values, setFieldValue, setFieldTouched, setValues, isSubmitting, isValid, dirty }) => {
          const invoiceRegionOptions: Option[] = values.invoiceCountryOption?.value
            ? regions
                .filter((r) => r.countryId === values.invoiceCountryOption?.value)
                .map((r) => ({ value: r.id ?? '', label: r.name } as Option))
            : [];

          return (
            <FormGrid noValidate>
              <div style={{ display: 'none' }}>
                <TextInput name="needDataUpdating" hidden />
              </div>

              <SelectDataField
                label={t('invoiceAddressCountry')}
                placeholder={t('placeholder.select')}
                name="invoiceCountryOption"
                required
                isMulti={false}
                value={values.invoiceCountryOption}
                onChange={(value: Option | null) => {
                  handleCountryChange(value as Option, values, setValues, setFieldValue);
                  // Validation already ran as part of setValues above; avoid a second, stale pass.
                  setFieldTouched('vat', true, false);
                }}
                options={countries.map((c) => ({ value: c.id, label: c.name } as Option))}
                shouldShowBottomError={(err) => err !== REQUIRED_MSG}
              />

              <InputFormField
                label={t('invoiceOrganizationName')}
                name="invoiceOrganizationName"
                placeholder={t('placeholder.typeHere')}
                required
                {...invoiceFieldErrorProps}
              />

              {invoiceRegionOptions.length > 0 && (
                <SelectDataField
                  label="Invoice country region"
                  placeholder={t('placeholder.select')}
                  name="invoiceRegionOption"
                  required
                  isMulti={false}
                  value={values.invoiceRegionOption}
                  onChange={(value: Option | null) => setFieldValue('invoiceRegionOption', value)}
                  options={invoiceRegionOptions}
                  shouldShowBottomError={(err) => err !== REQUIRED_MSG}
                />
              )}

              <InputFormField
                label={t('invoiceAddressLine')}
                name="invoiceAddressLine"
                placeholder={t('placeholder.typeHere')}
                required
                {...invoiceFieldErrorProps}
              />

              <AvailableCountryCurrencies countries={countries} isRequired={false} />

              <InputFormField
                label={t('invoiceAddressLineSecond')}
                name="invoiceAddressLineSecond"
                placeholder={t('placeholder.typeHere')}
                {...invoiceFieldErrorProps}
              />

              <PaymentTerms isDisabled={!isAdmin} />

              <InputFormField
                label={t('invoiceAddressCity')}
                name="invoiceAddressCity"
                placeholder={t('placeholder.typeHere')}
                required
                {...invoiceFieldErrorProps}
              />

              <InputFormField
                label={t('invoicingEmail')}
                name="invoicingEmail"
                placeholder={t('placeholder.email')}
                trim
                required
                {...invoiceFieldErrorProps}
              />

              <InputFormField
                label={t('invoiceAddressZipCode')}
                name="invoiceAddressZipCode"
                placeholder={t('placeholder.typeHere')}
                required
                trim
                {...invoiceFieldErrorProps}
              />

              <InputFormField
                label={t('invoiceOrderingEmail')}
                name="invoiceOrderingEmail"
                placeholder={t('placeholder.email')}
                trim
                required
                {...invoiceFieldErrorProps}
              />

              <InputFormField
                label={t('vat')}
                name="vat"
                placeholder={t('placeholder.typeHere')}
                required={
                  EU_COUNTRIES_NAMES.includes(values.invoiceCountryOption?.label ?? '') ||
                  EU_COUNTRIES_NAMES.includes(values.invoiceRegionOption?.label ?? '')
                }
                {...vatFieldErrorProps}
              />

              <InputFormField
                label={t('invoiceRemindingEmail')}
                name="invoiceRemindingEmail"
                placeholder={t('placeholder.email')}
                trim
                required
                {...invoiceFieldErrorProps}
              />

              <SelectDataField
                label={t('isSpecialInvoice')}
                placeholder={t('placeholder.select')}
                name="isSpecialInvoiceNeedsOption"
                required
                isMulti={false}
                value={values.isSpecialInvoiceNeedsOption}
                onChange={(newValue: Option | null) => {
                  setFieldValue('isSpecialInvoiceNeedsOption', handleSpecialNeedsChange(newValue, setFieldValue));
                }}
                options={YesNoDataSource.map((c) => ({ value: c.value, label: c.label } as Option))}
                shouldShowBottomError={(err) => err !== REQUIRED_MSG}
              />

              <InputFormField
                label={t('invoiceCcEmail')}
                name="invoiceCcEmail"
                placeholder={t('placeholder.email')}
                trim
                {...invoiceFieldErrorProps}
              />

              {selectedCountry === 'Sweden' && (
                <InputFormField
                  label={t('gln')}
                  name="gln"
                  placeholder={t('placeholder.typeHere')}
                  {...invoiceFieldErrorProps}
                />
              )}

              {values.isSpecialInvoiceNeedsOption?.value === YES_NO.YES && (
                <InputFormField
                  label={t('organization.register.specialInvoiceNeeds')}
                  name="specialInvoiceNeeds"
                  placeholder={t('placeholder.typeHere')}
                  required
                  {...invoiceFieldErrorProps}
                />
              )}

              <SelectDataField
                label={t('isUniqueInvoicePurchaseOrderNumberRequired')}
                placeholder={t('placeholder.select')}
                name="isInvoiceUniquePurchaseOrderNumberOption"
                required
                isMulti={false}
                value={values.isInvoiceUniquePurchaseOrderNumberOption}
                onChange={(value: Option | null) => {
                  setFieldValue('isInvoiceUniquePurchaseOrderNumberOption', value);
                }}
                options={YesNoDataSource.map((c) => ({ value: c.value, label: c.label } as Option))}
                shouldShowBottomError={(err) => err !== REQUIRED_MSG}
              />

              <InputFormField
                label={t('invoiceReference')}
                name="reference"
                tooltip="Maximum 50 characters allowed"
                placeholder={t('placeholder.typeHere')}
                maxLength={50}
                {...invoiceFieldErrorProps}
              />

              <SaveButton disabled={isSubmitting || !isValid || !dirty} type="submit">
                {isSubmitting ? t('messages.saving') : t('messages.saveSettings')}
              </SaveButton>

              <div style={{ gridColumn: '1 / -1', padding: '10px 1rem 1rem' }}>
                {status === ProcessStatus.Error && <ErrorText>{t('manageSettings.errors.notSaved')}</ErrorText>}
                {status === ProcessStatus.Success && <SuccessText>{t('manageSettings.messages.saved')}</SuccessText>}
              </div>
            </FormGrid>
          );
        }}
      </Formik>
    </>
  );
};

const FormGrid = styled(Form)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: start;

  @media (max-width: 1001px) {
    grid-template-columns: 1fr;
  }
`;

const InvoiceInfoHelpBox = styled(HelpBox)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 13px 16px;
`;

const UpdateButton = styled(PrimeButton)`
  min-height: 22px;
  padding: 0px 6px;
  border-radius: 2px;
  white-space: normal;
  flex-shrink: 0;

  background-color: ${(p) => p.theme.colors.primaryOrange};
  border-color: ${(p) => p.theme.colors.primaryOrange};
  color: ${(p) => p.theme.colors.primaryWhite};
  font-family: Inter;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0px;
  vertical-align: middle;

  display: flex;
  justify-content: center;
  text-align: center;

  &&:hover {
    background-color: ${(p) => p.theme.colors.elementsButtonHover};
    border-color: ${(p) => p.theme.colors.elementsButtonHover};
    color: ${(p) => p.theme.colors.primaryWhite};
  }
`;

const ActionUpdateButton = withActionState(UpdateButton);

export default ManageInvoiceInfo;
